import {
  useCompleteHarnessOAuthAuthorization,
  useConnectHarnessCredential,
  useCreateHarnessOAuthAuthorization,
  useHarnessCredentials,
  useHarnessOAuthAuthorization,
  useRevokeHarnessCredential,
} from "@/api/harnessCredentials";
import {
  chipButtonClassName,
  chipDangerButtonClassName,
  GroupLabel,
  LcBadge,
  NESTED_DIALOG_Z,
  PaneBody,
  paneFieldInputClassName,
  Row,
  smButtonClassName,
} from "@/components/AdminPanel/Organization/components/manage-pane-primitives";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/utils";
import {
  HarnessCredential,
  HarnessCredentialKind,
  HarnessCredentialProvider,
  HarnessOAuthAuthorization,
} from "@cohesive/api-client";
import { Spinner } from "@cohesive/ui-components";
import { formatDistanceToNow } from "date-fns";
import { Bot, Sparkles } from "lucide-react";
import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";

/**
 * Model providers — whose provider bill an agent turn is spent against.
 *
 * The roster's shape, one row per provider rather than one per credential:
 * only one credential is ever in force for a provider, and which one is the
 * point (yours beats your organization's, and both beat Cohesive's). A row
 * therefore reads as an answer to "who pays for this", with the connect and
 * disconnect verbs hung off it.
 */

const PROVIDERS: {
  id: HarnessCredentialProvider;
  name: string;
  icon: typeof Bot;
  /** Offered in this order, and the first is what the connect dialog opens on. */
  kinds: HarnessCredentialKind[];
  placeholder: string;
  /** What the subscription is called where the user bought it. */
  subscriptionLabel: string;
}[] = [
  {
    id: "anthropic",
    name: "Anthropic",
    icon: Sparkles,
    kinds: ["subscription", "api_key"],
    placeholder: "sk-ant-…",
    subscriptionLabel: "Claude Pro or Max",
  },
  {
    id: "openai",
    name: "OpenAI",
    icon: Bot,
    kinds: ["subscription", "api_key"],
    placeholder: "sk-proj-…",
    subscriptionLabel: "ChatGPT Plus or Pro",
  },
];

const KIND_LABELS: Record<HarnessCredentialKind, string> = {
  api_key: "API Key",
  subscription: "Subscription",
};

/**
 * The row's second line: which credential this provider's turns actually spend. Exported because
 * the precedence it describes (yours, then your organization's, then Cohesive's) is the whole
 * point of the pane and worth asserting without a render.
 */
export function describeCredential(
  credential?: HarnessCredential,
  inherited?: HarnessCredential,
): string {
  if (credential?.status === "error") {
    // resolveProvider drops a non-active row, so turns fall through the rest of the precedence
    // exactly as if this one were never connected — say which key that leaves in force, the same
    // way the inherited branch below does. The trailing period goes so a provider-supplied
    // sentence joins cleanly.
    const reason = (
      credential.status_detail ?? "The provider rejected this credential"
    ).replace(/\.$/, "");
    const fallback =
      inherited && inherited.status !== "error"
        ? `your organization's key ending ${inherited.tail}`
        : "Cohesive's key";
    return `${reason} — turns run on ${fallback}`;
  }
  if (credential) {
    return `${KIND_LABELS[credential.kind]} ${nameAccount(credential)} · connected ${formatDistanceToNow(
      new Date(credential.connected_at),
      { addSuffix: true },
    )}`;
  }
  if (inherited) {
    // resolveProvider drops a non-active row, so an errored org key is not what turns spend.
    return inherited.status === "error"
      ? "Your organization's key was rejected — turns run on Cohesive's key"
      : `Using your organization's key ending ${inherited.tail}`;
  }
  return "Using Cohesive's key";
}

/**
 * Whose account this is, in the most recognisable form the provider gave us: the email, plus the
 * workspace where the provider names one. A credential connected before we collected either — and
 * any API key, which is issued to nobody in particular — falls back to the token's last four.
 *
 * Anthropic auto-names a personal subscription's organization after the account, so a workspace
 * that only restates the email is dropped rather than printed twice. The stored value stays.
 */
function nameAccount(credential: HarnessCredential): string {
  const { account_email: email, account_workspace: workspace } = credential;
  if (!email) return `ending ${credential.tail}`;
  if (!workspace || workspace === `${email}'s Organization`) return email;
  return `${email} · ${workspace}`;
}

const API_KEY_HINT =
  "Paste a key from the provider's console. It is checked against the provider before it is stored.";

/**
 * Signing in to a subscription. The user never leaves this dialog for a terminal — they open the
 * provider's own window and carry one code across the gap, in whichever direction that provider
 * goes:
 *
 * `paste_code` (Anthropic) shows the code on its own page for the user to bring back here.
 * `device_code` (ChatGPT) takes a code from the user instead, because OpenAI pins its redirect to
 * localhost and a web app has no way to receive one — so we show the code and poll.
 */
function SubscriptionSignIn({
  provider,
  onConnected,
}: {
  provider: (typeof PROVIDERS)[number];
  onConnected: () => void;
}) {
  const [authorization, setAuthorization] =
    useState<HarnessOAuthAuthorization>();
  const [code, setCode] = useState("");
  const start = useCreateHarnessOAuthAuthorization();
  const complete = useCompleteHarnessOAuthAuthorization();
  const polled = useHarnessOAuthAuthorization(authorization);

  // The device flow lands without anything happening in this dialog: the user finishes at the
  // provider and the poll finds the credential.
  React.useEffect(() => {
    if (polled.data?.harnessCredential) onConnected();
  }, [polled.data?.harnessCredential, onConnected]);

  if (!authorization) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-n-50">
          Sign in with your {provider.subscriptionLabel} subscription. Your
          agent turns run on it instead of Cohesive&apos;s credential, and it
          stays connected — no token to re-paste.
        </p>
        <Button
          disabled={start.isPending}
          isLoading={start.isPending}
          onClick={async () => {
            try {
              const started = await start.mutateAsync(provider.id);
              setAuthorization(started);
              window.open(
                started.authorize_url,
                "_blank",
                "noopener,noreferrer",
              );
            } catch {
              toast.error(
                `Could not reach ${provider.name}. Please try again.`,
              );
            }
          }}
        >
          Connect {provider.name}
        </Button>
      </div>
    );
  }

  if (authorization.mode === "device_code") {
    // Only the provider's own verdict ends this: the server records it as a failed status, and a
    // device code expires on its own. A fetch that failed says something about the network, and
    // retracting a live sign-in over one would throw away a code the user may already have entered.
    const failed = polled.data?.authorization.status === "failed";

    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-n-50">
          Enter this code at{" "}
          <a
            className="underline"
            href={authorization.authorize_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {new URL(authorization.authorize_url).host}
          </a>{" "}
          and approve the sign-in. This window will finish on its own.
        </p>
        <p className="text-center font-mono text-2xl tracking-widest">
          {authorization.user_code}
        </p>
        {failed ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-n-50">
              That sign-in did not complete. Start again.
            </p>
            <Button
              variant="outline"
              onClick={() => setAuthorization(undefined)}
            >
              Start again
            </Button>
          </div>
        ) : (
          <p className="flex items-center gap-2 text-sm text-n-50">
            <Spinner /> Waiting for you to approve it…
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-n-50">
        Approve the sign-in in the {provider.name} window, then paste the whole
        code it shows here.{" "}
        <a
          className="underline"
          href={authorization.authorize_url}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open it again
        </a>{" "}
        if it did not appear.
      </p>
      <Input
        id="harness-oauth-code"
        autoComplete="off"
        spellCheck={false}
        className={paneFieldInputClassName}
        placeholder="Paste the code"
        value={code}
        onChange={(event) => setCode(event.target.value)}
      />
      <Button
        disabled={!code.trim() || complete.isPending}
        isLoading={complete.isPending}
        onClick={async () => {
          try {
            await complete.mutateAsync({
              authorizationId: authorization.id,
              code: code.trim(),
            });
            onConnected();
          } catch (error) {
            const message = (
              error as {
                response?: { data?: { error?: { message?: string } } };
              }
            )?.response?.data?.error?.message;
            toast.error(message || "That code was not accepted.");
          }
        }}
      >
        Finish connecting
      </Button>
    </div>
  );
}

function ConnectDialog({
  provider,
  open,
  onOpenChange,
}: {
  provider: (typeof PROVIDERS)[number];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const kinds = provider.kinds;
  const [kind, setKind] = useState<HarnessCredentialKind>(kinds[0]);
  const [secret, setSecret] = useState("");
  const connect = useConnectHarnessCredential();

  const submit = async () => {
    try {
      await connect.mutateAsync({
        provider: provider.id,
        kind,
        secret: secret.trim(),
      });
      onOpenChange(false);
      setSecret("");
      toast.success(`${provider.name} connected.`);
    } catch (error) {
      // The connect endpoint makes one live provider call before storing, so a
      // rejected key is reported here rather than mid-turn.
      const message = (
        error as { response?: { data?: { error?: { message?: string } } } }
      )?.response?.data?.error?.message;
      toast.error(
        message || `Failed to connect ${provider.name}. Please try again.`,
      );
    }
  };

  // A connect in flight cannot be cancelled, so dismissing would only hide it: the key still gets
  // stored and swept out to the sandboxes. Hold the dialog until it lands either way.
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !connect.isPending && onOpenChange(next)}
    >
      <DialogContent
        className={cn("sm:max-w-[480px]", NESTED_DIALOG_Z)}
        overlayClassName={NESTED_DIALOG_Z}
      >
        <DialogHeader>
          <DialogTitle>Connect {provider.name}</DialogTitle>
          <DialogDescription>
            Your agent turns run on this instead of Cohesive&apos;s credential.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {kinds.length > 1 && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="harness-credential-kind">Credential Type</Label>
              <Select
                value={kind}
                onValueChange={(value) =>
                  setKind(value as HarnessCredentialKind)
                }
              >
                <SelectTrigger
                  id="harness-credential-kind"
                  className={paneFieldInputClassName}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={NESTED_DIALOG_Z}>
                  {kinds.map((value) => (
                    <SelectItem key={value} value={value}>
                      {KIND_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {kind === "subscription" ? (
            <SubscriptionSignIn
              provider={provider}
              onConnected={() => {
                onOpenChange(false);
                toast.success(`${provider.name} connected.`);
              }}
            />
          ) : (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="harness-credential-secret">API Key</Label>
              <Input
                id="harness-credential-secret"
                type="password"
                autoComplete="off"
                spellCheck={false}
                className={paneFieldInputClassName}
                placeholder={provider.placeholder}
                value={secret}
                onChange={(event) => setSecret(event.target.value)}
              />
              <p className="text-sm text-n-50">{API_KEY_HINT}</p>
            </div>
          )}

          {/* On the form rather than in a footnote: the credential is applied
              by a third party the user did not choose, which sees the plaintext
              of every request it is attached to. */}
          <p className="text-sm text-n-50">
            Cohesive stores this encrypted and never writes it into your
            sandbox. It is applied to outbound requests at the sandbox network
            boundary, which our infrastructure provider operates — they hold the
            credential and see the requests it is attached to.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            disabled={connect.isPending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          {kind !== "subscription" && (
            <Button
              disabled={!secret.trim() || connect.isPending}
              isLoading={connect.isPending}
              onClick={submit}
            >
              Connect
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevokeDialog({
  credential,
  providerName,
  open,
  onOpenChange,
}: {
  credential: HarnessCredential;
  providerName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { mutateAsync: revoke, isPending } = useRevokeHarnessCredential();

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !isPending && onOpenChange(next)}
    >
      <DialogContent
        className={cn("sm:max-w-[425px]", NESTED_DIALOG_Z)}
        overlayClassName={NESTED_DIALOG_Z}
      >
        <DialogHeader>
          <DialogTitle>Disconnect {providerName}</DialogTitle>
          <DialogDescription>
            Sandboxes already running stop using it within moments, and later
            turns fall back to Cohesive&apos;s credential. This cannot be undone
            — reconnecting means pasting the credential again.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            disabled={isPending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            className="bg-spectrum-red text-paper hover:bg-[color-mix(in_oklab,var(--spectrum-red)_88%,var(--ink))]"
            disabled={isPending}
            isLoading={isPending}
            onClick={async () => {
              try {
                await revoke(credential);
                onOpenChange(false);
                toast.success(`${providerName} disconnected.`);
              } catch {
                toast.error(
                  `Failed to disconnect ${providerName}. Please try again.`,
                );
              }
            }}
          >
            Disconnect
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ModelProviders: React.FC = () => {
  const { data: credentials, isLoading, isError } = useHarnessCredentials();
  const [connecting, setConnecting] = useState<
    (typeof PROVIDERS)[number] | null
  >(null);
  const [revoking, setRevoking] = useState<{
    credential: HarnessCredential;
    providerName: string;
  } | null>(null);

  /**
   * The user endpoint returns both scopes. Connecting is user-scoped only for now, but an org
   * credential still has to be read: it is what a member without their own key spends, and a row
   * that did not name it would read as "you are on Cohesive's key".
   */
  const byScope = useMemo(() => {
    const map = new Map<string, HarnessCredential>();
    for (const credential of credentials ?? []) {
      map.set(`${credential.scope}:${credential.provider}`, credential);
    }
    return map;
  }, [credentials]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    );
  }

  // Without this a failed list renders every provider as unconnected, which reads as "you are on
  // Cohesive's key" — the one thing this pane exists to state.
  if (isError) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-n-50">
        Could not load model providers. Refresh to try again.
      </div>
    );
  }

  const providerRow = (provider: (typeof PROVIDERS)[number]) => {
    const Icon = provider.icon;
    const credential = byScope.get(`user:${provider.id}`);
    const inherited = byScope.get(`org:${provider.id}`);
    const hasError = credential?.status === "error";

    return (
      <Row
        key={provider.id}
        tile={<Icon className="size-5" />}
        name={provider.name}
        meta={describeCredential(credential, inherited)}
      >
        {hasError && <LcBadge tone="blocked">Error</LcBadge>}
        {credential ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              className={chipButtonClassName}
              onClick={() => setConnecting(provider)}
            >
              Replace
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={chipDangerButtonClassName}
              onClick={() =>
                setRevoking({ credential, providerName: provider.name })
              }
            >
              Disconnect
            </Button>
          </>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            className={cn("shrink-0", smButtonClassName)}
            onClick={() => setConnecting(provider)}
          >
            Connect
          </Button>
        )}
      </Row>
    );
  };

  return (
    <PaneBody>
      <section className="flex flex-col gap-0.5">
        <GroupLabel>Your credentials</GroupLabel>
        {PROVIDERS.map(providerRow)}
      </section>

      {connecting && (
        <ConnectDialog
          open
          provider={connecting}
          onOpenChange={(open) => {
            if (!open) setConnecting(null);
          }}
        />
      )}

      {revoking && (
        <RevokeDialog
          open
          credential={revoking.credential}
          providerName={revoking.providerName}
          onOpenChange={(open) => {
            if (!open) setRevoking(null);
          }}
        />
      )}
    </PaneBody>
  );
};

export default ModelProviders;
